import React from 'react';
import { useTranslation } from 'react-i18next';
import PriceCard from '../components/PriceCard';
import TitleWithLanterns from '../components/TitleWithLanterns';
import MenuCarousel from '../components/MenuCarousel';
import Reveal, { RevealDivider } from '../components/Reveal';
import { Fish, Soup, Pizza, Beef, Shell, Cookie, PackageOpen, AlertTriangle, Clock, CreditCard, Info } from 'lucide-react';

const Menu: React.FC = () => {
  const { t } = useTranslation();

  const schedule = [
    { day: t('hours.monday'), lunch: t('hours.lunch'), dinner: t('hours.dinner') },
    { day: t('hours.tuesday'), lunch: t('hours.lunch'), dinner: t('hours.dinner') },
    { day: t('hours.wednesday'), lunch: t('hours.lunch'), dinner: t('hours.dinner') },
    { day: t('hours.thursday'), lunch: t('hours.lunch'), dinner: t('hours.dinner') },
    { day: t('hours.friday'), lunch: t('hours.lunch'), dinner: t('hours.dinnerFriday') },
    { day: t('hours.saturday'), lunch: t('hours.lunchWeekend'), dinner: t('hours.dinnerSaturday') },
    { day: t('hours.sunday'), lunch: t('hours.lunchWeekend'), dinner: t('hours.dinner') }
  ];

  const cuisineTypes = [
    { icon: <Fish size={48} />, key: 'japanese' },
    { icon: <Soup size={48} />, key: 'chinese' },
    { icon: <Pizza size={48} />, key: 'european' },
    { icon: <Beef size={48} />, key: 'grill' },
    { icon: <Shell size={48} />, key: 'seafood' },
    { icon: <Cookie size={48} />, key: 'desserts' }
  ];

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="relative h-[50vh] flex items-center justify-center overflow-hidden bg-luxury-black">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-40"
          style={{
            backgroundImage: `url('/restaurant_photos/menu_photo.JPG')`,
          }}
        />
        <div className="absolute inset-0 bg-luxury-gradient" />
        <TitleWithLanterns
          title={t('nav.menu')}
          subtitle={t('menu.subtitle')}
          lanternType={2}
        />
      </section>

      {/* Menu PDFs Carousel */}
      <MenuCarousel />

      {/* Takeaway Rules */}
      <section className="py-8 md:py-14 lg:py-20 bg-luxury-cream relative">
        <div className="section-padding relative z-10">
          <Reveal className="text-center mb-8 md:mb-12 lg:mb-20">
            <p className="text-luxury-gold text-lg tracking-luxury uppercase font-body mb-4">
              <PackageOpen className="inline-block mr-2 mb-1" size={20} />
              {t('takeaway.subtitle')}
            </p>
            <h2 className="text-display-md md:text-display-lg font-display mb-6 text-luxury-black">
              {t('takeaway.title')}
            </h2>
            <RevealDivider className="mx-auto w-20" delay={0.15} />
          </Reveal>

          <Reveal delay={0.1} className="max-w-3xl mx-auto">
            <div className="border border-luxury-gold/30 rounded-lg p-6 md:p-8 bg-white/70">
              <div className="text-center mb-8">
                <span className="text-display-md font-display text-luxury-gold">8,90€</span>
                <p className="text-lg text-luxury-gray-dark font-body mt-2">{t('takeaway.price')}</p>
              </div>

              <div className="border-t border-luxury-gold/20 pt-8">
                <div className="flex items-center gap-2 mb-6">
                  <AlertTriangle className="text-luxury-gold flex-shrink-0" size={24} />
                  <h3 className="text-xl font-display text-luxury-black">{t('takeaway.rulesTitle')}</h3>
                </div>
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <span className="text-luxury-gold mt-1">•</span>
                    <span className="text-luxury-gray-dark font-body">{t('takeaway.rule1')}</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-luxury-gold mt-1">•</span>
                    <span className="text-luxury-gray-dark font-body">{t('takeaway.rule2')}</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-luxury-gold mt-1">•</span>
                    <span className="text-luxury-gray-dark font-body">{t('takeaway.rule3')}</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-luxury-gold mt-1">•</span>
                    <span className="text-luxury-gray-dark font-body">{t('takeaway.rule4')}</span>
                  </li>
                </ul>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Cuisine Types */}
      <section className="py-8 md:py-14 lg:py-20 bg-luxury-cream relative">
        {/* Cherry Blossom Decoration Background */}
        <img
          src="/restaurant_photos/cherry_blossom1.png"
          alt=""
          className="absolute right-0 top-[20%] -translate-y-1/2 w-[70%] md:w-[55%] lg:w-[45%] h-auto opacity-10 pointer-events-none z-0"
        />
        <div className="section-padding relative z-10">
          <Reveal className="text-center mb-8 md:mb-12 lg:mb-20">
            <p className="text-luxury-gold text-lg tracking-luxury uppercase font-body mb-4">{t('menu.specialties')}</p>
            <h2 className="text-display-md md:text-display-lg font-display mb-6 text-luxury-black">
              {t('menu.worldCuisines')}
            </h2>
            <RevealDivider className="mx-auto w-20" delay={0.15} />
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {cuisineTypes.map((cuisine, index) => (
              <Reveal key={index} delay={index * 0.08}>
                <div className="luxury-card p-6 md:p-8 lg:p-10 text-center hover-lift group h-full">
                  <div className="text-luxury-gold mb-6 group-hover:text-luxury-black transition-colors duration-300 flex justify-center">
                    {cuisine.icon}
                  </div>
                  <h3 className="text-2xl font-display mb-4 text-luxury-black">
                    {t(`menu.cuisine.${cuisine.key}.name`)}
                  </h3>
                  <p className="text-luxury-gray-dark font-body">
                    {t(`menu.cuisine.${cuisine.key}.description`)}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Buffet Features */}
      <section className="py-8 md:py-14 lg:py-20 bg-white relative">
        {/* Cherry Blossom 2 Decoration - Right Side Mirrored */}
        <img
          src="/restaurant_photos/cherry_blossom2.png"
          alt=""
          className="absolute left-0 top-[20%] -translate-y-1/2 w-[70%] md:w-[55%] lg:w-[45%] h-auto opacity-10 pointer-events-none z-0" style={{ transform: 'translateY(-50%) scaleX(-1) scaleY(-1)' }}
        />

        <div className="section-padding relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <Reveal>
              <p className="text-luxury-gold text-lg tracking-luxury uppercase font-body mb-4">
                {t('menu.buffetFormula')}
              </p>
              <h2 className="text-display-md md:text-display-lg font-display mb-6 md:mb-8 lg:mb-12 text-luxury-black">
                {t('menu.allYouCanEat')}
              </h2>
            </Reveal>
            <RevealDivider className="mx-auto mb-6 md:mb-8 lg:mb-12 w-20" delay={0.15} />

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 mb-8 md:mb-12 lg:mb-16">
              <Reveal delay={0}>
                <div className="text-center">
                  <div className="text-display-md font-display text-luxury-gold mb-4">600+</div>
                  <p className="text-lg text-luxury-gray-dark font-body">{t('menu.dishes')}</p>
                </div>
              </Reveal>
              <Reveal delay={0.08}>
                <div className="text-center">
                  <div className="text-display-md font-display text-luxury-gold mb-4">{t('menu.homemade')}</div>
                </div>
              </Reveal>
              <Reveal delay={0.16}>
                <div className="text-center">
                  <div className="text-display-md font-display text-luxury-gold mb-4">{t('menu.openDays')}</div>
                  <p className="text-lg text-luxury-gray-dark font-body">{t('menu.open')}</p>
                </div>
              </Reveal>
            </div>

            <Reveal delay={0.1}>
              <p className="text-xl text-luxury-gray-dark leading-relaxed font-body">
                {t('menu.buffetDescription')}
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Prices Reminder */}
      <section className="py-8 md:py-14 lg:py-20 bg-luxury-cream relative overflow-hidden">
        {/* Decorative background elements */}
        <div className="absolute top-0 left-0 w-64 h-64 bg-luxury-gold/10 rounded-full -ml-32 -mt-32" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-luxury-gold/10 rounded-full -mr-48 -mb-48" />

        <div className="section-padding relative z-10">
          <Reveal className="text-center mb-8 md:mb-12 lg:mb-20">
            <p className="text-luxury-gold text-lg tracking-luxury uppercase font-body mb-4">Prix</p>
            <h2 className="text-display-md md:text-display-lg font-display mb-6 text-luxury-black">
              {t('prices.title')}
            </h2>
            <RevealDivider className="mx-auto w-20" delay={0.15} />
          </Reveal>

          {/* Adult Prices */}
          <div className="mb-16">
            <Reveal><h3 className="text-2xl font-display text-luxury-gold text-center mb-10">{t('menu.adultPrices')}</h3></Reveal>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 max-w-6xl mx-auto">
              <Reveal delay={0}><PriceCard title={t('prices.weekdayLunch')} price="€19.90" /></Reveal>
              <Reveal delay={0.08}><PriceCard title={t('prices.weekdayDinner')} price="€26.90" /></Reveal>
              <Reveal delay={0.16}><PriceCard title={t('prices.saturdayLunch')} price="€27.90" /></Reveal>
              <Reveal delay={0.24}><PriceCard title={t('prices.weekendDinner')} price="€28.90" highlight /></Reveal>
            </div>
          </div>

          {/* Children Prices */}
          <div className="mb-12">
            <Reveal><h3 className="text-2xl font-display text-luxury-gold text-center mb-10">{t('menu.childrenPrices')}</h3></Reveal>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 max-w-4xl mx-auto">
              <Reveal delay={0}><PriceCard title={t('prices.child35')} price="€8.90" /></Reveal>
              <Reveal delay={0.08}><PriceCard title={t('prices.child69')} price="€12.90" /></Reveal>
              <Reveal delay={0.16}><PriceCard title={t('prices.childUnder3')} price={t('prices.free')} /></Reveal>
            </div>
          </div>

          <Reveal>
            <p className="text-center text-luxury-gray mt-8 text-sm font-body italic">
              {t('prices.disclaimer')}
            </p>
          </Reveal>

          {/* Waste Policy Warning */}
          <Reveal delay={0.05} className="mt-8 max-w-2xl mx-auto">
            <div className="border border-luxury-gold/30 rounded-lg p-6 bg-white text-center shadow-sm">
              <p className="text-luxury-black font-body text-base mb-2">
                {t('prices.wasteWarning')}
              </p>
              <p className="text-luxury-gray-dark font-body text-sm">
                {t('prices.noTakeaway')}
              </p>
            </div>
          </Reveal>

          {/* Practical Info Cards */}
          <div className="mt-16 max-w-5xl mx-auto">
            <div className="border-t border-luxury-gold/20 pt-12">
              <Reveal>
                <h4 className="text-3xl md:text-4xl font-display text-luxury-gold text-center mb-12">{t('menu.practicalInfo')}</h4>
              </Reveal>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
                {/* Hours Card */}
                <Reveal delay={0}>
                  <div className="bg-white border border-luxury-gold/20 rounded-lg p-6 text-center hover:border-luxury-gold/40 transition-colors duration-300 shadow-sm h-full">
                    <Clock className="mx-auto text-luxury-gold mb-4" size={32} />
                    <h5 className="text-lg font-display text-luxury-gold mb-3">{t('menu.hours')}</h5>
                    <p className="text-luxury-black font-body text-sm">{t('info.open7days')}</p>
                    <p className="text-luxury-gray-dark font-body text-xs mt-2">{t('menu.lunchDinnerHours')}</p>
                  </div>
                </Reveal>

                {/* Payment Card */}
                <Reveal delay={0.08}>
                  <div className="bg-white border border-luxury-gold/20 rounded-lg p-6 text-center hover:border-luxury-gold/40 transition-colors duration-300 shadow-sm h-full">
                    <CreditCard className="mx-auto text-luxury-gold mb-4" size={32} />
                    <h5 className="text-lg font-display text-luxury-gold mb-3">{t('menu.acceptedPayment')}</h5>
                    <p className="text-luxury-black font-body text-sm">{t('info.payment')}</p>
                  </div>
                </Reveal>

                {/* Info Card */}
                <Reveal delay={0.16}>
                  <div className="bg-white border border-luxury-gold/20 rounded-lg p-6 text-center hover:border-luxury-gold/40 transition-colors duration-300 shadow-sm h-full">
                    <Info className="mx-auto text-luxury-gold mb-4" size={32} />
                    <h5 className="text-lg font-display text-luxury-gold mb-3">{t('menu.practicalInfo')}</h5>
                    <p className="text-luxury-black font-body text-sm">
                      {t('info.halal')}
                    </p>
                    <p className="text-luxury-black font-body text-sm mt-1">
                      {t('info.noReservation')}
                    </p>
                    <p className="text-luxury-black font-body text-sm mt-1">
                      {t('info.parking')}
                    </p>
                  </div>
                </Reveal>
              </div>
            </div>

            {/* Opening Hours Table */}
            <Reveal delay={0.1} className="max-w-4xl mx-auto">
              <div className="bg-white rounded-lg shadow-xl overflow-hidden border border-luxury-gold/20">
                {/* Table Header */}
                <div className="grid grid-cols-3 bg-luxury-gold/10 border-b border-luxury-gold/20">
                  <div className="p-4 font-display text-luxury-gold text-sm uppercase tracking-wider">Jour</div>
                  <div className="p-4 text-center font-display text-luxury-gold text-sm uppercase tracking-wider">Midi</div>
                  <div className="p-4 text-center font-display text-luxury-gold text-sm uppercase tracking-wider">Soir</div>
                </div>
                {schedule.map((item, index) => (
                  <div
                    key={index}
                    className={`grid grid-cols-3 ${index % 2 === 0 ? 'bg-white' : 'bg-luxury-cream'} hover:bg-luxury-gold/5 transition-colors duration-200`}
                  >
                    <div className="p-4 font-body text-lg text-luxury-black">{item.day}</div>
                    <div className="p-4 text-center text-lg text-luxury-black">{item.lunch}</div>
                    <div className="p-4 text-center text-lg text-luxury-black">{item.dinner}</div>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Menu;
